import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import FlexLib from '@flex/lib';
import { Button, Container, Grid, Tooltip, FormControlLabel, Switch } from '@mui/material';
import { Topheader } from '../../components/Topheader';
import invoicesService from '../../services/invoicesService';
import TableV2, { spreadPaginationOptions } from '../../components/Table/TableV2';
import { DeleteButton, NotificationStatus, PaymentStatus } from '../../components/Table/components';
import { useTranslation } from 'react-i18next';
import ScheduleInvoicesModal from './components/ScheduleInvoicesModal';
import BatchInvoicesModal from './components/BatchInvoicesModal';
import { FlexAlerts, AlertSeverityTypes } from '../../components/Message/Alerts/Alerts';
import {
  getInvoiceFirstName,
  getInvoiceLastName,
  DEFAULT_DAYS_FROM,
  PAYMENT_TYPES,
  getPaymentStatus,
  getPaymentType,
  PAYMENT_STATUS_MAP,
} from './Common';
const { formatCurrency } = FlexLib.utils.feeService;

const InvoiceTable = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isScheduleInvoicesModelOpen, setIsScheduleInvoicesModelOpen] = useState(false);
  const [isBatchInvoicesModelOpen, setIsBatchInvoicesModelOpen] = useState(false);
  const [generatedSelfPayLink, setGeneratedSelfPayLink] = useState('');

  const getLocationId = () => {
    let location = localStorage.getItem('locationPaydetails');
    location = JSON.parse(location) || {};
    return location?._id;
  };
  const [locationId] = useState(getLocationId);
  const [isTowingBuild, setIsTowingBuild] = useState(false);
  const [isUserAllowedToDelete, setIsUserAllowedToDelete] = useState(false);
  const [isUserAllowedToExport, setIsUserAllowedToExport] = useState(false);
  const [invoiceLabel, setInvoiceLabel] = useState('Invoice');
  const [invoiceRows, setInvoiceRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);

  const [paginationOptions, setPaginationOptions] = useState({});
  const [isShowArchivedOn, setIsShowArchivedOn] = useState(false);

  useEffect(() => {
    let location = localStorage.getItem('locationPaydetails');
    location = JSON.parse(location) || {};
    setIsTowingBuild(!!location?.customTowingBuild);
    const customInvoiceField = location?.customFields?.find((field) => field?.enabled === true);
    if (customInvoiceField?.value) {
      setInvoiceLabel(customInvoiceField.value);
    }

    let user = localStorage.getItem('user');
    user = JSON.parse(user) || {};
    setIsUserAllowedToDelete(!!user?.permissions?.deleteInvoice);
    setIsUserAllowedToExport(!!user?.permissions?.exportRecord);
  }, []);

  /**
   * @param {import('../../../../types/common').PaginationOptions} paginationOptions
   * @param {boolean} showArchived
   */
  const getInvoices = (paginationOptions, showArchived) => {
    if (locationId && typeof paginationOptions.page === 'number') {
      setIsLoading(true);

      const query = {
        ...spreadPaginationOptions(paginationOptions),
        locationId,
        includeArchived: showArchived,
      };

      invoicesService
        .getLocationInvoicesList(query)
        .then((response) => {
          setInvoiceRows(response?.data?.invoices ?? []);
          setRowCount(response?.data?.rowCount ?? 0);
          setMessages([]);
        })
        .catch((error) => {
          setMessages([
            {
              message: `Could not retrieve invoices. ${error?.response?.data?.message ?? ''}`,
              severity: AlertSeverityTypes.error,
            },
          ]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const formattedRows = useMemo(() => {
    return invoiceRows.map((invoice) => {
      return {
        ...invoice,
        id: invoice._id,
        customerFirstName: getInvoiceFirstName(invoice),
        customerLastName: getInvoiceLastName(invoice),
        locationName: invoice.locationId?.locationName,
        paymentType: getPaymentType(invoice),
        paymentStatus: getPaymentStatus(invoice),
      };
    });
  }, [invoiceRows]);

  const openInvoice = (invoiceId, rowData) => {
    if (rowData?.submitedToPax) {
      history.push('/EditInvoices/' + invoiceId);
    } else if (isTowingBuild) {
      history.push('/InvoiceEdit/' + invoiceId);
    } else if (['deleted', 'paid', 'submitted', 'rejected'].includes(rowData?.paymentStatus?.toLowerCase())) {
      history.push('/EditInvoices/' + invoiceId);
    } else if (['archived', 'pending', 'pastdue', 'in queue'].includes(rowData?.paymentStatus?.toLowerCase())) {
      history.push({
        pathname: '/AddInvoices',
        state: {
          rowData: {
            ...rowData,
            email: rowData.email || undefined,
            customerLastName: rowData.customerLastName || undefined,
          },
        },
      });
    } else {
      history.push('/Terminal/?' + invoiceId);
    }
  };

  const openAddInvoice = () => {
    if (isTowingBuild) {
      history.push('/RequestForm');
    } else {
      history.push('/AddInvoices');
    }
  };

  const generateSelfPayLink = () => {
    setGeneratedSelfPayLink(`${window.location.origin}/selfPayment/${locationId}`);
  };

  const handleSwitchShowArchived = (event) => {
    setIsShowArchivedOn(event?.target?.checked);
    getInvoices(paginationOptions, event?.target?.checked);
  };

  const deleteInvoice = (invoiceId) => {
    setIsLoading(true);
    invoicesService
      .deleteInvoice(invoiceId)
      .then(() => {
        setMessages([
          {
            message: 'Invoice deleted successfully.',
            severity: AlertSeverityTypes.success,
          },
        ]);
        getInvoices(paginationOptions, isShowArchivedOn);
      })
      .catch((error) => {
        setMessages([
          {
            message: `Could not delete invoice. ${error?.response?.data?.message}`,
            severity: AlertSeverityTypes.error,
          },
        ]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDeleteInvoice = (invoiceId) => {
    if (!invoiceId) {
      setMessages([
        {
          message: `Could not delete invoice. Need an invoice id.`,
          severity: AlertSeverityTypes.warning,
        },
      ]);
      return;
    }

    if (window.confirm('Are you sure want to delete the item?')) {
      deleteInvoice(invoiceId);
    }
  };

  /**
   * @param {import('../../../../types/common').PaginationOptions} newPaginationOptions
   */
  const fetchInvoices = (newPaginationOptions) => {
    setPaginationOptions(newPaginationOptions);
    getInvoices(newPaginationOptions, isShowArchivedOn);
  };

  const columns = [
    {
      field: 'invoice',
      type: 'string',
      headerName: invoiceLabel ?? 'Invoice',
      minWidth: 150,
      flex: 1,
      renderCell: (params) => (
        <Tooltip title="View Details">
          <span data-cy="invoice-link" className="link" onClick={(_e) => openInvoice(params.row._id, params.row)}>
            {params.value}
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'customerFirstName',
      type: 'string',
      minWidth: 150,
      flex: 1,
      headerName: 'First Name',
    },
    {
      field: 'customerLastName',
      type: 'string',
      minWidth: 150,
      flex: 1,
      headerName: 'Last Name',
    },
    {
      field: 'amount',
      type: 'number',
      headerName: 'Amount',
      minWidth: 100,
      flex: 1,
      renderCell: (params) => <span>{formatCurrency(params.value)}</span>,
    },
    {
      field: 'total',
      type: 'number',
      headerName: 'Total',
      minWidth: 100,
      flex: 1,
      renderCell: (params) => <span>{formatCurrency(params.value)}</span>,
    },
    {
      field: 'locationName',
      type: 'string',
      headerName: 'Location',
      sortable: false,
      minWidth: 100,
      flex: 1,
    },
    {
      field: 'paymentType',
      type: 'singleSelect',
      minWidth: 125,
      flex: 1,
      valueOptions: [...Object.values(PAYMENT_TYPES)],
      headerName: 'Payment Type',
    },
    {
      field: 'emailSent',
      type: 'boolean',
      minWidth: 115,
      flex: 1,
      headerName: 'Email Status',
      renderCell: (params) => <NotificationStatus isSent={params.value} />,
    },
    {
      field: 'textSent',
      type: 'boolean',
      headerName: 'Text Status',
      minWidth: 115,
      flex: 1,
      renderCell: (params) => <NotificationStatus isSent={params.value} />,
    },
    {
      field: 'paymentStatus',
      type: 'singleSelect',
      valueOptions: PAYMENT_STATUS_MAP,
      headerName: 'Payment Status',
      minWidth: 130,
      flex: 1,
      renderCell: (params) => <PaymentStatus status={params.value} />,
    },
    {
      field: 'dateOpened',
      type: 'date',
      headerName: 'Date',
      minWidth: 100,
      flex: 1,
      valueGetter: (value) => new Date(value),
      renderCell: (params) => <span>{new Date(params.value).toLocaleDateString()}</span>,
    },
    {
      field: 'dueDate',
      type: 'date',
      headerName: 'Due Date',
      minWidth: 100,
      flex: 1,
      valueGetter: (value) => (value ? new Date(value) : null),
      renderCell: (params) => <span>{params.value ? new Date(params.value).toLocaleDateString() : ''}</span>,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      minWidth: 100,
      flex: 1,
      renderCell: (params) =>
        isUserAllowedToDelete && (
          <DeleteButton id={`delete-${params.row._id}`} onClick={() => handleDeleteInvoice(params.row._id)} />
        ),
    },
  ];

  return (
    <div className="rightPanel">
      <Topheader />
      <div className="contentPart">
        <Container
          component="main"
          maxWidth={false}
          sx={{ marginBottom: '40px' }}
          className="terminal-container term-add"
        >
          {!isTowingBuild && (
            <ScheduleInvoicesModal
              isOpen={isScheduleInvoicesModelOpen}
              onClose={() => setIsScheduleInvoicesModelOpen(false)}
              locationId={locationId}
              invoiceLabel={invoiceLabel}
            />
          )}
          <BatchInvoicesModal
            isOpen={isBatchInvoicesModelOpen}
            onClose={() => setIsBatchInvoicesModelOpen(false)}
            locationId={locationId}
          />
          <Grid container rowSpacing={2} columnSpacing={3} alignItems={'flex-start'}>
            <Grid item xs={12} />
            <Grid item xs={12}>
              <h3 className="terminal-title">Invoices</h3>
            </Grid>
            <Grid item xs={12}>
              <FlexAlerts messages={messages} />
            </Grid>
            <Grid item xs={12}>
              <TableV2
                rows={formattedRows}
                columns={columns}
                fetchData={fetchInvoices}
                rowCount={rowCount}
                isLoading={isLoading}
                dateConfig={{
                  isDateFiltering: true,
                  defaultFromDateDays: DEFAULT_DAYS_FROM,
                }}
                searchConfig={{
                  isSearchBar: true,
                }}
                defaultSortColumn="dateOpened"
                isExportable={isUserAllowedToExport}
                toolbarExtension={
                  <>
                    <Button id="add-invoice-button" data-cy="add-invoice-button" onClick={openAddInvoice}>
                      Add Invoice
                    </Button>
                    <Button
                      id="batch-invoices-button"
                      className="tableToolbarBtn"
                      onClick={() => setIsScheduleInvoicesModelOpen(true)}
                    >
                      {t('SCHEDULEINVOICES')}
                    </Button>
                    <Button
                      id="bulk-invoices-button"
                      className="tableToolbarBtn"
                      onClick={() => setIsBatchInvoicesModelOpen(true)}
                    >
                      {t('BATCHINVOICES')}
                    </Button>

                    {generatedSelfPayLink ? (
                      <input
                        id="self-pay-link-input"
                        className="tableToolbarLink"
                        type="text"
                        value={generatedSelfPayLink}
                        readOnly
                      />
                    ) : (
                      <Button id="static-pay-link-button" className="tableToolbarBtn" onClick={generateSelfPayLink}>
                        Static Pay Link
                      </Button>
                    )}
                    <FormControlLabel
                      style={{ marginLeft: '5px' }}
                      control={<Switch checked={isShowArchivedOn} onChange={handleSwitchShowArchived} />}
                      label="Show Archived"
                    />
                  </>
                }
              />
            </Grid>
          </Grid>
        </Container>
      </div>
    </div>
  );
};

export default InvoiceTable;
